"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { useAppState } from "@/app/hooks/useAppState";
import { staggerContainerVariants, staggerItemVariants } from "@/lib/animations";
import { normalizeQuantityForDisplay } from "@/lib/categorization/utils";
import type { DashboardEntry } from "@/lib/dashboard/server";

import { RecipeLinkRow } from "./RecipeLinkRow";

interface ShoppingCheckProps {
  weekEntries: DashboardEntry[];
}

interface MatchedRecipe {
  id: string;
  title: string;
  image: string | null;
}

interface MatchedItem {
  id: string;
  listItemText: string;
  quantity?: string;
  matchedRecipes: MatchedRecipe[];
}

/** A planned recipe and the shopping-list items it needs, each as the list shows it (quantity first). */
interface RecipeNeeds extends MatchedRecipe {
  items: string[];
}

/** One row per recipe, in the order the recipes first appear in the item list. */
function groupByRecipe(matches: MatchedItem[]): RecipeNeeds[] {
  const byRecipe = new Map<string, RecipeNeeds>();
  for (const match of matches) {
    const label = match.quantity
      ? `${normalizeQuantityForDisplay(match.quantity)} ${match.listItemText}`
      : match.listItemText;
    for (const recipe of match.matchedRecipes) {
      const row = byRecipe.get(recipe.id) ?? { ...recipe, items: [] };
      row.items.push(label);
      byRecipe.set(recipe.id, row);
    }
  }
  return [...byRecipe.values()];
}

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\d.,]+\s*(g|kg|ml|l|oz|lb|lbs|cup|cups|tbsp|tsp|pcs?|bunch|bunches)\b/gi, "")
    .replace(/[^a-z一-鿿぀-ゟ゠-ヿ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function textsMatch(listText: string, recipeText: string): boolean {
  const a = normalizeText(listText);
  const b = normalizeText(recipeText);
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

export function ShoppingCheck({ weekEntries }: ShoppingCheckProps) {
  const t = useTranslations("dashboard");
  const { state } = useAppState();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const matches = useMemo(() => {
    if (!mounted) return [];

    const recipeIngredients: {
      text: string;
      recipeId: string;
      recipeTitle: string;
      recipeImage: string | null;
    }[] = [];
    for (const entry of weekEntries) {
      for (const ing of entry.recipe.ingredients) {
        recipeIngredients.push({
          text: ing.text,
          recipeId: entry.recipe.id,
          recipeTitle: entry.recipe.title,
          recipeImage: entry.recipe.image,
        });
      }
    }

    const activeList = state.lists.find((l) => l.id === state.activeListId) ?? state.lists[0];
    if (!activeList) return [];

    const activeItems = activeList.ingredients.filter((i) => !i.completed);
    if (activeItems.length === 0) return [];

    const matched: MatchedItem[] = [];
    for (const item of activeItems) {
      const recipes = new Map<string, MatchedRecipe>();
      for (const ri of recipeIngredients) {
        if (textsMatch(item.text, ri.text)) {
          recipes.set(ri.recipeId, {
            id: ri.recipeId,
            title: ri.recipeTitle,
            image: ri.recipeImage,
          });
        }
      }
      if (recipes.size > 0) {
        matched.push({
          id: item.id,
          listItemText: item.text,
          quantity: item.quantity,
          matchedRecipes: [...recipes.values()],
        });
      }
    }

    return matched;
  }, [weekEntries, state, mounted]);

  if (weekEntries.length === 0) return null;
  if (!mounted) {
    return (
      <section aria-label={t("groceryCheck")} className="w-full @md:w-[500px] @md:shrink-0">
        <div className="mb-3">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("groceryCheck")}
          </p>
          <h3 className="text-base font-semibold">{t("groceryCheckSubtitle")}</h3>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="h-24" />
        </div>
      </section>
    );
  }

  const recipeRows = groupByRecipe(matches);
  const activeList = state.lists.find((l) => l.id === state.activeListId) ?? state.lists[0];
  const hasListItems = activeList && activeList.ingredients.some((i) => !i.completed);

  return (
    <section aria-label={t("groceryCheck")} className="w-full @md:w-[500px] @md:shrink-0">
      <div className="mb-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("groceryCheck")}
        </p>
        <h3 className="text-base font-semibold">{t("groceryCheckSubtitle")}</h3>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        {matches.length > 0 ? (
          <>
            <p className="mb-2 text-sm text-muted-foreground">
              {t("itemsMatched", { count: matches.length })}
            </p>
            <motion.ul
              variants={staggerContainerVariants(0.04)}
              initial="hidden"
              animate="visible"
              className="-mb-1 flex flex-col gap-1"
            >
              {recipeRows.map((recipe) => (
                <motion.li key={recipe.id} variants={staggerItemVariants}>
                  <RecipeLinkRow
                    href={`/recipes/${recipe.id}`}
                    title={recipe.title}
                    image={recipe.image}
                    detail={recipe.items.join(t("itemsSeparator"))}
                  />
                </motion.li>
              ))}
            </motion.ul>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <p className="text-muted-foreground">
              {!hasListItems ? t("noListItems") : t("noMatches")}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
